import type { GridColDef } from '@mui/x-data-grid-premium';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { SavePreferenceFormData } from '../../types/savePreference';
import type { FavouriteColumnPresetModel } from '../columnViewTypes';
import { stripFavouriteFields } from '../columnVisibilityFavouriteUtils';
import { useColumnViews } from '../useColumnViews';
import type { GridSettingsPreference } from './gridSettings/types';

type UseDataGridColumnViewsParams = {
    enabled: boolean;
    gridId: string;
    columns: GridColDef[];
    trackColumnOrder?: boolean;
    trackColumnVisibility?: boolean;
    getCurrentGridSettings?: () => GridSettingsPreference | undefined;
};

type ColumnOrderApi = {
    applyOrderedFieldsToGrid: (orderedFields: string[] | null | undefined) => void;
    getCurrentOrderedFields: () => string[];
};

/**
 * Saved column-view prefs + dirty state + options for `useColumnVisibility`.
 *
 * Ref bridge (`bindColumnOrderApi` / `syncFromVisibility`): visibility runs before the
 * order hook inside `useDataGridColumnViewFeature`, so apply/get order APIs are wired
 * through refs rather than direct returns.
 */
export function useDataGridColumnViews({
    enabled,
    gridId,
    columns,
    trackColumnOrder = false,
    trackColumnVisibility = false,
    getCurrentGridSettings
}: UseDataGridColumnViewsParams) {
    const columnVisibilityModelRef = useRef<FavouriteColumnPresetModel>({});
    const selectedSavedViewIdRef = useRef<string | null>(null);
    const columnOrderApiRef = useRef<ColumnOrderApi>({
        applyOrderedFieldsToGrid: () => undefined,
        getCurrentOrderedFields: () => []
    });

    const [columnOrderRevision, setColumnOrderRevision] = useState(0);
    const [columnOrderDirty, setColumnOrderDirty] = useState(false);

    const defaultOrderedFields = useMemo(
        () => (columns ?? []).map((column) => column.field).filter(Boolean),
        [columns]
    );

    const bumpColumnOrderRevision = useCallback(() => {
        setColumnOrderRevision((revision) => revision + 1);
    }, []);

    const { savedViews, saveView, deleteView, savingView, deletingView } = useColumnViews(gridId, enabled);

    const handleSaveColumnView = useCallback(
        async (formData: SavePreferenceFormData) => {
            const saved = await saveView({
                ...formData,
                columnVisibilityModel: stripFavouriteFields(columnVisibilityModelRef.current ?? {}),
                orderedFields: trackColumnOrder ? columnOrderApiRef.current.getCurrentOrderedFields() : [],
                gridSettings: getCurrentGridSettings?.()
            });
            if (saved) {
                setColumnOrderDirty(false);
            }
            return saved;
        },
        [getCurrentGridSettings, saveView, trackColumnOrder]
    );

    const handleUpdateColumnView = useCallback(async () => {
        const selectedId = selectedSavedViewIdRef.current;
        const existing = savedViews.find((view) => view.id === selectedId);
        if (!existing) {
            return null;
        }
        const saved = await saveView({
            name: existing.name,
            description: existing.description,
            shared: existing.shared,
            preferenceId: existing.id,
            accountId: existing.accountId,
            columnVisibilityModel: stripFavouriteFields(columnVisibilityModelRef.current ?? {}),
            orderedFields: trackColumnOrder
                ? columnOrderApiRef.current.getCurrentOrderedFields()
                : existing.orderedFields,
            gridSettings: getCurrentGridSettings ? getCurrentGridSettings() : existing.gridSettings
        });
        if (saved) {
            setColumnOrderDirty(false);
        }
        return saved;
    }, [getCurrentGridSettings, savedViews, saveView, trackColumnOrder]);

    const visibilityOptions = useMemo(
        () => ({
            enableSaveColumnView: enabled,
            trackColumnVisibility,
            trackColumnOrder,
            savedColumnViews: savedViews,
            onSaveColumnView: handleSaveColumnView,
            onUpdateColumnView: handleUpdateColumnView,
            onDeleteColumnView: deleteView,
            savingColumnView: savingView,
            deletingColumnView: deletingView,
            getCurrentOrderedFields: () => columnOrderApiRef.current.getCurrentOrderedFields(),
            defaultOrderedFields,
            columnOrderRevision,
            columnOrderDirty,
            onOrderedFieldsApplied: (orderedFields: string[] | null | undefined) => {
                columnOrderApiRef.current.applyOrderedFieldsToGrid(orderedFields);
            }
        }),
        [
            columnOrderDirty,
            columnOrderRevision,
            defaultOrderedFields,
            deleteView,
            deletingView,
            enabled,
            handleSaveColumnView,
            trackColumnOrder,
            trackColumnVisibility,
            handleUpdateColumnView,
            savedViews,
            savingView
        ]
    );

    const syncFromVisibility = useCallback(
        (columnVisibilityModel: FavouriteColumnPresetModel, selectedSavedViewId: string | null) => {
            columnVisibilityModelRef.current = columnVisibilityModel;
            selectedSavedViewIdRef.current = selectedSavedViewId;
        },
        []
    );

    const bindColumnOrderApi = useCallback((api: ColumnOrderApi) => {
        columnOrderApiRef.current = api;
    }, []);

    return {
        visibilityOptions,
        syncFromVisibility,
        bindColumnOrderApi,
        setColumnOrderDirty,
        bumpColumnOrderRevision
    };
}
